/**
 * Debug utilities for React frontend development
 */
import React from 'react'

// Check if we're in development mode
export const isDev = import.meta.env.DEV

// Enhanced console logging with component context
export const debug = {
  log: (message, data = null, component = '') => {
    if (isDev) {
      const prefix = component ? `[${component}]` : '[DEBUG]'
      console.log(`${prefix} ${message}`, data || '')
    }
  },
  
  warn: (message, data = null, component = '') => {
    if (isDev) {
      const prefix = component ? `[${component}]` : '[WARN]'
      console.warn(`${prefix} ${message}`, data || '')
    }
  },
  
  error: (message, error = null, component = '') => {
    if (isDev) {
      const prefix = component ? `[${component}]` : '[ERROR]'
      console.error(`${prefix} ${message}`, error || '')
    }
  },
  
  api: (endpoint, response = null, method = 'GET') => {
    if (isDev) {
      console.group(`[API] ${method} ${endpoint}`)
      if (response) {
        console.log('Response:', response)
      }
      console.groupEnd()
    }
  },
  
  component: (componentName, action, data = null) => {
    if (isDev) {
      console.log(`[${componentName}] ${action}`, data || '')
    }
  },
  
  // Performance timing
  time: (label) => {
    if (isDev) {
      console.time(`[PERF] ${label}`)
    }
  },
  
  timeEnd: (label) => {
    if (isDev) {
      console.timeEnd(`[PERF] ${label}`)
    }
  }
}

// Debug panel component (can be added to any component during development)
export const DebugPanel = ({ data, title = 'Debug Data' }) => {
  if (!isDev) return null
  
  return (
    <div style={{
      position: 'fixed',
      top: '10px',
      right: '10px',
      background: '#1a1a1a',
      color: '#00ff00',
      padding: '10px',
      borderRadius: '5px',
      fontSize: '12px',
      maxWidth: '400px',
      maxHeight: '300px',
      overflow: 'auto',
      zIndex: 9999,
      fontFamily: 'monospace'
    }}>
      <h4 style={{ color: '#ffff00', marginTop: 0 }}>{title}</h4>
      <pre>{JSON.stringify(data, null, 2)}</pre>
    </div>
  )
}

// React DevTools helpers
export const withDebugProps = (Component) => {
  if (!isDev) return Component
  
  return (props) => {
    // Add debug props to component
    const debugProps = {
      ...props,
      __debug: {
        componentName: Component.name || 'Unknown',
        props: props,
        timestamp: Date.now()
      }
    }
    
    return <Component {...debugProps} />
  }
}

// Performance monitoring hook
export const useDebugRender = (componentName) => {
  if (!isDev) return
  
  const renderCount = React.useRef(0)
  const startTime = React.useRef(Date.now())
  
  React.useEffect(() => {
    renderCount.current++
    const endTime = Date.now()
    console.log(`[RENDER] ${componentName} rendered ${renderCount.current} times (${endTime - startTime.current}ms)`)
  })
}

// Global debug object for browser console
if (isDev && typeof window !== 'undefined') {
  window.__KAM_DEBUG__ = {
    debug,
    isDev,
    version: '1.0.0',
    
    // Helper to inspect React Fiber
    findReactFiber: (element) => {
      for (let key in element) {
        if (key.startsWith('__reactInternalInstance$') || key.startsWith('__reactFiber$')) {
          return element[key]
        }
      }
      return null
    },
    
    // Helper to get component state/props from DOM element
    getReactProps: (element) => {
      const fiber = window.__KAM_DEBUG__.findReactFiber(element)
      return fiber ? { props: fiber.memoizedProps, state: fiber.memoizedState } : null
    }
  }
  
  console.log('🚀 KAM Debug tools loaded. Use window.__KAM_DEBUG__ in console.')
}

export default debug
